/**
 * 現在編集中シーンのインメモリストア
 *
 * Phase 9 で IndexedDB に永続化する際、保存対象として使われる。
 * Phase 5 の段階では「撮影 → MAPPING → EXPOSURE」で使うワーキングセット。
 *
 * Shoot {
 *   id: string,
 *   createdAt: number,
 *   updatedAt: number,
 *   presetId: string,
 *
 *   // 撮影時のスナップショット（プリセットと独立に編集可能）
 *   camera, filmFormat, lens, filmId, ei, filterId,
 *   defaultDeveloperId,
 *
 *   // 写真
 *   originalImageBlob: Blob,
 *   croppedImageBlob:  Blob,
 *   monoImageBlob:     Blob,
 *   cropBox:  { x, y, w, h }, // クロップ後画像内基準
 *   meta:     { orientation, sourceW, sourceH },
 *
 *   // ピン
 *   spots: [SpotMeasurement],
 *
 *   // 計算結果（exposure.js から書き戻す）
 *   baseShootEV, aperture, shutterSpeed,
 *   shadowAnchorId, highlightAnchorId,
 *   developmentPlan,
 *
 *   // 振り返り (Phase 8)
 *   development: {...},
 *   printing: {...},
 *
 *   notes,
 *   title, location, weather,
 *
 *   // v2 で追加（すべて任意。無ければ従来の既定値で動く）
 *   meterIso,      // L-858D に設定した ISO（無ければプリセットの EI）
 *   templateId,    // 選んだシーンテンプレート（旧 sceneTemplate も読む）
 *   devTempC,      // 現像の温度（無ければ 20.0）
 *   devNSec,       // レシピで適用した N 時間（秒）。無ければ現像時間 DB
 *   recipeId,      // 適用中のレシピ
 *   filterComp,    // フィルター倍数を露出に加えるか（無ければ加えない＝旧データの計算どおり）
 *   expoRange,     // カメラ・レンズの露出の範囲 { fastest, slowest, widest, smallest }（無ければ全範囲）
 *   shotExposure,  // 撮影した露出（確定記録。後で計算し直さない）{ aperture, shutter, evSet, ei, filterStops, plan, bracketStops, at }
 *   devAgitation,  // 攪拌の型 standard / gentle / aggressive / stand（無ければ standard）
 * }
 *
 * SpotMeasurement {
 *   id: string,
 *   x: number,    // モノクロ画像内の正規化座標 0-1
 *   y: number,
 *   meterEV: number,
 *   placedZone: number,   // 0-10
 *   fallenZone: number | null,
 *   label: string,
 *   role: 'shadow-anchor' | 'highlight-anchor' | null,
 *   reading?: { mode: 'sek', shutter, tenths, step, aperture, iso }  // L-858D の表示値
 * }
 *
 * 自動保存：シーンが変わるたびに 0.6 秒後に IndexedDB へ保存する（下書き含む）。
 * ピンが変わると露出の計算結果（撮影 EV・絞り・シャッター・現像方針）も書き直す。
 */

import { saveShoot } from './store.js';
import { derivedShootFields } from './zone-calc.js';

let current = null;
const spotUndo = []; // 29. ピンの操作の取り消し用（直前の状態）
const subscribers = new Set();
let saveTimer = null;
let savedListeners = new Set();

export function setCurrentShoot(shoot) {
  current = shoot;
  spotUndo.length = 0;
  emit();
}

export function getCurrentShoot() {
  return current;
}

export function clearCurrentShoot() {
  clearTimeout(saveTimer);
  current = null;
  spotUndo.length = 0;
  emit(false);
}

export function updateCurrentShoot(patch) {
  if (!current) return;
  current = { ...current, ...patch, updatedAt: Date.now() };
  if (patch.spots || 'filterComp' in patch || 'filterId' in patch) current = withDerived(current);
  emit();
}

export function subscribe(fn) {
  subscribers.add(fn);
  return () => subscribers.delete(fn);
}

/** 保存が終わったとき（「自動保存済み」表示用） */
export function onSaved(fn) {
  savedListeners.add(fn);
  return () => savedListeners.delete(fn);
}

function emit(save = true) {
  subscribers.forEach((fn) => {
    try { fn(current); } catch (e) { console.warn('[scene] subscriber error:', e); }
  });
  if (save && current) scheduleSave();
}

function persistable(shoot) {
  return {
    ...shoot,
    spots: (shoot.spots || []).filter((s) => !s._draft).map(({ _draft, ...rest }) => rest),
  };
}

function withDerived(shoot) {
  const committed = { ...shoot, spots: (shoot.spots || []).filter((s) => !s._draft) };
  return { ...shoot, ...derivedShootFields(committed) };
}

function scheduleSave() {
  clearTimeout(saveTimer);
  saveTimer = setTimeout(() => { flushSave().catch(() => {}); }, 600);
}

/** すぐに保存する（現像の記録、画面を閉じるときなど） */
/**
 * 8. 保存の状態。失敗したら saveErrorListeners に知らせ、少し待って自動で再試行する。
 *   成功したら onSaved に知らせる（失敗の表示を消す）。
 */
const saveErrorListeners = new Set();
let retryTimer = null;
let retryCount = 0;

export function onSaveError(fn) {
  saveErrorListeners.add(fn);
  return () => saveErrorListeners.delete(fn);
}

/** すぐに保存する（現像の記録、画面を閉じるときなど） */
export async function flushSave() {
  clearTimeout(saveTimer);
  if (!current) return;
  try {
    await saveShoot(persistable(current));
    clearTimeout(retryTimer);
    retryCount = 0;
    savedListeners.forEach((fn) => { try { fn(); } catch {} });
  } catch (e) {
    console.warn('[scene] autosave failed:', e);
    const quota = e?.name === 'QuotaExceededError' || /quota/i.test(String(e?.message || ''));
    saveErrorListeners.forEach((fn) => { try { fn({ quota, retryCount }); } catch {} });
    // 自動の再試行（5 秒・15 秒・45 秒）。容量不足は待っても直らないので試さない
    clearTimeout(retryTimer);
    if (!quota && retryCount < 3) {
      const wait = 5000 * Math.pow(3, retryCount);
      retryCount++;
      retryTimer = setTimeout(() => { flushSave().catch(() => {}); }, wait);
    }
    throw e;
  }
}

/** 「もう一度保存」ボタン用。自動の再試行の回数を戻してから試す */
export function retrySave() {
  retryCount = 0;
  return flushSave();
}

// --------------------------------------------------
// シーン初期化（撮影完了時に呼ばれる）
// --------------------------------------------------
export function initShootFromCapture({ preset, originalBlob, croppedBlob, monoBlob, cropBox, meta, gps, capturedAt }) {
  const now = capturedAt || Date.now();
  const shoot = {
    id: `s_${now.toString(36)}_${Math.random().toString(36).slice(2, 7)}`,
    createdAt: now,
    updatedAt: now,
    presetId: preset.id,
    gps: gps || null,
    environment: null,  // 天候/月齢/日の出 (非同期で後埋め)

    // プリセット情報スナップショット（都度上書き可）
    camera: preset.camera,
    filmFormat: preset.filmFormat,
    lens: preset.defaultLens,
    filmId: preset.filmId,
    ei: preset.ei,
    filterId: preset.filterId,
    defaultDeveloperId: preset.defaultDeveloperId,
    defaultShadowZone: preset.defaultShadowZone,
    defaultHighlightZone: preset.defaultHighlightZone,
    meterIso: preset.ei,
    expoRange: preset.expoRange || null, // 4. カメラ・レンズの露出の範囲（プリセットの写し）
    filterComp: !!preset.filterId, // 2. フィルター倍数を露出に加える（新しいシーンはフィルターがあれば既定でオン）

    originalImageBlob: originalBlob,
    croppedImageBlob: croppedBlob,
    monoImageBlob: monoBlob,
    cropBox,
    meta,

    spots: [],

    baseShootEV: null,
    aperture: null,
    shutterSpeed: null,
    shadowAnchorId: null,
    highlightAnchorId: null,
    developmentPlan: null,

    development: null,
    printing: null,

    title: '',
    location: '',
    weather: '',
    notes: '',
  };
  setCurrentShoot(shoot);
  return shoot;
}

// --------------------------------------------------
// SpotMeasurement 操作
// --------------------------------------------------
export function addSpot(spot) {
  if (!current) return null;
  const id = `pin_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 5)}`;
  const newSpot = {
    id,
    x: 0.5,
    y: 0.5,
    meterEV: 12,
    placedZone: 5,
    fallenZone: null,
    label: '',
    role: null,
    ...spot,
  };
  current = withDerived({
    ...current,
    spots: [...current.spots, newSpot],
    updatedAt: Date.now(),
  });
  emit();
  return newSpot;
}

export function updateSpot(id, patch) {
  if (!current) return;
  let spots = current.spots.map((s) => (s.id === id ? { ...s, ...patch } : s));
  // role が main shadow / highlight になったら、既存の同役割ピンの役を外す
  if (patch.role === 'shadow-anchor' || patch.role === 'highlight-anchor') {
    spots = spots.map((s) => (s.id === id ? s : (s.role === patch.role ? { ...s, role: null } : s)));
  }
  current = withDerived({ ...current, spots, updatedAt: Date.now() });
  emit();
}

export function removeSpot(id) {
  if (!current) return;
  current = withDerived({
    ...current,
    spots: current.spots.filter((s) => s.id !== id),
    updatedAt: Date.now(),
  });
  emit();
}

export function getSpot(id) {
  if (!current) return null;
  return current.spots.find((s) => s.id === id) || null;
}

/** 確定済みのピン（編集中の新規ピンを除く） */
export function committedSpots(shoot = current) {
  return (shoot?.spots || []).filter((s) => !s._draft);
}

// --------------------------------------------------
// 29. ピンの取り消し
//   追加・編集・移動・削除の直前に pushSpotUndo を呼び、undoSpots で 1 つずつ戻す（最大 20）
// --------------------------------------------------
export function pushSpotUndo(label) {
  if (!current) return;
  spotUndo.push({ label, spots: JSON.parse(JSON.stringify(committedSpots(current))) });
  if (spotUndo.length > 20) spotUndo.shift();
}

export function canUndoSpots() {
  return spotUndo.length > 0 ? spotUndo[spotUndo.length - 1].label : null;
}

export function undoSpots() {
  if (!current || !spotUndo.length) return null;
  const { label, spots } = spotUndo.pop();
  current = withDerived({ ...current, spots, updatedAt: Date.now() });
  emit();
  return label;
}
